import Layout from "components/layout";
import Link from "next/link";

export default function TermosDeUso(): JSX.Element {
  return (
    <Layout
      title="Termos de Uso"
      description="Entenda as regras e condições para uso da plataforma"
    >
      <main>
        <div className="container">
          <h1>Termos de Uso</h1>
          <p>Entenda as regras e condições para uso da plataforma</p>

          <div className="content">
            <span className="caption">
              Última atualização: 22 de março de 2025
            </span>
            <p>
              Estes Termos de Uso constituem um acordo legal entre você e a
              Vstiba, estabelecendo as principais regras e condições que devem
              ser observadas por todos que acessam ou utilizam a plataforma
              Vstiba.
            </p>

            <p>
              Ao acessar, cadastrar-se ou utilizar qualquer funcionalidade
              disponibilizada pela plataforma, você automaticamente concorda com
              todas as regras e condições estabelecidas nestes Termos de Uso.
            </p>

            <h2>Definições</h2>
            <p>Para os fins destes Termos de Uso:</p>

            <ul>
              <li>
                <b>Vstiba</b> refere-se à plataforma educacional disponível no
                endereço https://www.vstiba.com.
              </li>

              <li>
                <b>Usuário</b> refere-se a qualquer pessoa que acesse, navegue,
                cadastre-se ou utilize qualquer funcionalidade da plataforma.
              </li>

              <li>
                <b>Conteúdo</b> refere-se a todo material disponibilizado na
                plataforma, incluindo, mas não limitado a: questões de
                vestibulares, textos, imagens, vídeos, áudios, resoluções,
                explicações e outros materiais educacionais.
              </li>

              <li>
                <b>Recurso de Chat</b> refere-se à funcionalidade que utiliza
                inteligência artificial para responder dúvidas sobre questões de
                vestibulares e suas resoluções.
              </li>
            </ul>

            <h2>Cadastro e Conta</h2>
            <p>
              Para utilizar todas as funcionalidades da plataforma, você deverá
              criar uma conta pessoal, fornecendo informações verdadeiras,
              precisas, atuais e completas sobre si mesmo, conforme solicitado
              no formulário de cadastro disponível.
            </p>

            <p>
              Você é o único responsável por manter a confidencialidade de sua
              senha e por todas as atividades realizadas com sua conta. Você
              concorda em notificar imediatamente a Vstiba sobre qualquer uso
              não autorizado de sua conta ou qualquer outra violação de
              segurança.
            </p>

            <p>
              A Vstiba se reserva o direito de recusar, suspender ou cancelar o
              seu cadastro, a qualquer momento e a seu exclusivo critério, sem a
              necessidade de aviso prévio, caso você viole qualquer disposição
              destes Termos de Uso ou utilize a plataforma de forma inadequada.
            </p>

            <h2>Uso da Plataforma</h2>
            <h3>Licença de Uso</h3>
            <p>
              A Vstiba concede ao Usuário uma licença pessoal, limitada, não
              exclusiva, não transferível e revogável para acessar e utilizar a
              plataforma, condicionada ao cumprimento destes Termos de Uso.
            </p>

            <h3>Restrições de Uso</h3>
            <p>Ao utilizar a plataforma, você concorda em não:</p>

            <ul>
              <li>
                Utilizar a plataforma para qualquer finalidade ilícita ou não
                autorizada;
              </li>

              <li>
                Violar qualquer legislação nacional ou internacional aplicável,
                incluindo leis de direitos autorais e propriedade intelectual;
              </li>

              <li>
                Modificar, adaptar, traduzir, fazer engenharia reversa,
                descompilar ou desmontar qualquer parte da plataforma;
              </li>

              <li>
                Reproduzir, duplicar, copiar, vender, comercializar ou revender
                qualquer parte do conteúdo da plataforma para qualquer fim, sem
                autorização prévia e expressa;
              </li>

              <li>
                Utilizar qualquer tipo de software, dispositivo ou outro meio
                para interferir ou tentar interferir no funcionamento adequado
                da plataforma;
              </li>

              <li>
                Usar mecanismos automatizados, incluindo &quot;robôs&quot;,
                &quot;spiders&quot; ou &quot;offline readers&quot; para acessar
                a plataforma de forma massiva;
              </li>

              <li>
                Transmitir vírus, trojans, worms ou qualquer outro código
                malicioso que possa danificar, interferir ou expor
                vulnerabilidades da plataforma;
              </li>

              <li>
                Compartilhar sua conta com terceiros ou permitir que terceiros
                acessem sua conta.
              </li>
            </ul>

            <h2>Recurso de Chat com Inteligência Artificial</h2>
            <p>
              A plataforma oferece um recurso de chat baseado em inteligência
              artificial, projetado para auxiliar os usuários na compreensão de
              questões de vestibulares e suas resoluções.
            </p>

            <h3>Finalidade e Uso Legítimo</h3>
            <p>
              O Recurso de Chat deve ser utilizado exclusivamente como
              ferramenta auxiliar no estudo e preparação para vestibulares,
              permitindo ao Usuário esclarecer dúvidas sobre questões e suas
              resoluções.
            </p>

            <p>
              O Usuário concorda em utilizar o Recurso de Chat de forma
              legítima, responsável e educacional, sem fazer uso abusivo ou
              excessivo da funcionalidade.
            </p>

            <h3>Limites e Restrições</h3>
            <p>
              A Vstiba se reserva o direito de estabelecer limites de utilização
              do Recurso de Chat, incluindo, mas não se limitando a:
            </p>

            <ul>
              <li>
                Número máximo de perguntas por dia ou período determinado;
              </li>

              <li>
                Limitação de recursos para usuários que demonstrem padrões de
                uso excessivo ou abusivo;
              </li>

              <li>
                Monitoramento de padrões de uso que possam indicar uso indevido
                da ferramenta.
              </li>
            </ul>

            <h3>Uso Abusivo</h3>
            <p>Considera-se uso abusivo do Recurso de Chat:</p>

            <ul>
              <li>Envio automatizado e massivo de perguntas;</li>

              <li>
                Utilização da ferramenta para finalidades diferentes do estudo e
                preparação para vestibulares;
              </li>

              <li>
                Tentativas de explorar, testar ou contornar as limitações da
                inteligência artificial;
              </li>

              <li>
                Uso da ferramenta para obtenção de vantagens indevidas em
                avaliações em curso;
              </li>

              <li>
                Compartilhamento da funcionalidade com terceiros não cadastrados
                na plataforma.
              </li>
            </ul>

            <p>
              A Vstiba se reserva o direito de suspender ou encerrar,
              permanentemente e sem aviso prévio, o acesso à plataforma de
              qualquer usuário que faça uso abusivo do Recurso de Chat ou de
              qualquer outra funcionalidade da plataforma, a critério exclusivo
              da Vstiba.
            </p>

            <h3>Limitações do Recurso de Chat</h3>
            <p>O Usuário reconhece e concorda que:</p>

            <ul>
              <li>
                O Recurso de Chat utiliza tecnologia de inteligência artificial
                que, embora avançada, pode conter imprecisões ou erros;
              </li>

              <li>
                As respostas fornecidas pelo Recurso de Chat são geradas
                automaticamente e devem ser utilizadas como material
                complementar de estudo, não substituindo a orientação de
                professores ou outras fontes oficiais de informação;
              </li>

              <li>
                A Vstiba não garante a perfeita precisão, completude ou
                utilidade das informações fornecidas pelo Recurso de Chat.
              </li>
            </ul>

            <h2>Propriedade Intelectual</h2>
            <p>
              Todo o conteúdo disponibilizado na plataforma, incluindo, mas não
              se limitando a, textos, gráficos, logotipos, ícones, imagens,
              fotografias, clips de áudio, downloads digitais e compilações de
              dados, é propriedade exclusiva da Vstiba ou de seus fornecedores
              de conteúdo e está protegido pelas leis nacionais e internacionais
              de propriedade intelectual.
            </p>

            <p>
              Você não pode copiar, reproduzir, republicar, fazer upload,
              postar, transmitir, distribuir ou modificar o conteúdo disponível
              na plataforma sem o consentimento prévio e por escrito da Vstiba.
            </p>

            <p>
              A utilização de qualquer conteúdo disponibilizado na plataforma
              para fins comerciais é expressamente proibida.
            </p>

            <h2>Disponibilidade da Plataforma</h2>
            <p>
              A Vstiba envidará seus melhores esforços para manter a plataforma
              disponível de forma contínua e ininterrupta. No entanto, o Usuário
              reconhece e concorda que poderão ocorrer interrupções no acesso à
              plataforma em razão de problemas técnicos, casos fortuitos, força
              maior ou por qualquer outro motivo alheio à vontade da Vstiba.
            </p>

            <p>
              A Vstiba se reserva o direito de modificar, suspender ou
              descontinuar, temporária ou permanentemente, a plataforma ou
              qualquer parte dela, com ou sem aviso prévio, não sendo devida
              qualquer indenização ao Usuário.
            </p>

            <h2>Privacidade</h2>
            <p>
              A Vstiba se compromete a proteger a privacidade dos Usuários, de
              acordo com a sua Política de Privacidade, disponível em{" "}
              <Link href="/privacidade">
                <a>https://www.vstiba.com/privacidade</a>
              </Link>
              .
            </p>

            <p>
              Ao utilizar a plataforma, o Usuário concorda com a coleta, uso e
              armazenamento de suas informações, conforme descrito na Política
              de Privacidade.
            </p>

            <h2>Isenção de Responsabilidade</h2>
            <p>
              A Vstiba não garante que as informações contidas na plataforma
              sejam precisas, completas ou atuais. O conteúdo é fornecido apenas
              para fins educacionais e informativos, e a Vstiba não assume
              qualquer responsabilidade pelo uso dessas informações pelo
              Usuário.
            </p>

            <p>
              A Vstiba não será responsável por quaisquer danos ou prejuízos
              decorrentes do uso ou incapacidade de uso da plataforma,
              incluindo, mas não se limitando a, danos por perda de lucros,
              interrupção de negócios, perda de informações ou quaisquer outros
              danos pecuniários.
            </p>

            <h2>Modificações nos Termos de Uso</h2>
            <p>
              A Vstiba se reserva o direito de alterar estes Termos de Uso a
              qualquer momento, a seu exclusivo critério. As alterações entrarão
              em vigor imediatamente após a publicação dos Termos de Uso
              atualizados na plataforma.
            </p>

            <p>
              O uso continuado da plataforma após a publicação das alterações
              constituirá aceitação de tais alterações. Recomendamos que você
              verifique periodicamente estes Termos de Uso para se manter
              informado sobre eventuais alterações.
            </p>

            <h2>Disposições Gerais</h2>
            <p>
              A tolerância quanto ao eventual descumprimento de quaisquer das
              disposições destes Termos de Uso não constituirá renúncia ao
              direito de exigir o cumprimento da obrigação, nem perdão, nem
              alteração do que foi aqui contratado.
            </p>

            <p>
              Caso qualquer disposição destes Termos de Uso seja considerada
              ilegal, nula ou inexequível por qualquer razão, as disposições
              restantes não serão afetadas e permanecerão válidas e aplicáveis
              na máxima extensão possível.
            </p>

            <p>
              Estes Termos de Uso constituem o acordo integral entre o Usuário e
              a Vstiba em relação ao uso da plataforma, substituindo todos os
              acordos anteriores.
            </p>

            <h2>Legislação Aplicável e Foro</h2>
            <p>
              Estes Termos de Uso serão regidos e interpretados de acordo com as
              leis da República Federativa do Brasil.
            </p>

            <p>
              Fica eleito o foro da Comarca da cidade de Curitiba, Estado do
              Paraná, como competente para dirimir quaisquer controvérsias
              decorrentes destes Termos de Uso, independentemente de qualquer
              outro, por mais privilegiado que seja ou venha a ser.
            </p>

            <h2>Contato</h2>
            <p>
              Se você tiver alguma dúvida sobre estes Termos de Uso, entre em
              contato conosco através do link{" "}
              <Link href="/contato">
                <a>https://www.vstiba.com/contato</a>
              </Link>
              .
            </p>
          </div>
        </div>
      </main>

      <style jsx>{`
        h2 {
          @apply mt-8;
        }
        h3 {
          @apply mt-6;
        }
        h4 {
          @apply mt-4;
        }
        ul {
          @apply list-disc ml-4;
        }
        ul li {
          @apply mb-2 last:mb-0;
        }
        li b {
          margin-right: 4px;
        }
      `}</style>
    </Layout>
  );
}
